import type { CSSProperties } from 'react'
import faqarrow from '../../../assets/images/icons/faqarrow.svg'
import type { SectionTheme } from '../../theme/sectionTheme'
import colors from '../../theme/color'

type QuestionItemProps = {
  question: string
  answer: string
  theme?: SectionTheme
  isOpen: boolean
  onToggle: () => void
}

const QuestionItem = ({ question, answer, theme = 'default', isOpen, onToggle }: QuestionItemProps) => {
  const themeStyles: Record<
    SectionTheme,
    {
      cardStyle: CSSProperties
      iconOpenColor: string
    }
  > = {
    default: {
      cardStyle: {
        backgroundImage: `linear-gradient(to right, ${colors.faqGradientStart}, ${colors.faqGradientEnd})`,
      },
      iconOpenColor: colors.purple,
    },
    greentheme: {
      cardStyle: { backgroundColor: `${colors.portfolioGreen}06` },
      iconOpenColor: colors.portfolioGreen,
    },
    rabbitxtheme: {
      cardStyle: { backgroundColor: `${colors.rabbitXAccent}06` },
      iconOpenColor: colors.rabbitXAccent,
    },
    arbventuretheme: {
      cardStyle: { backgroundColor: `${colors.arbVentureThemeAccent}0A` },
      iconOpenColor: colors.arbVentureThemeAccent,
    },
    lightpinktheme: {
      cardStyle: { backgroundColor: `${colors.lightPinkSecondary}0A` },
      iconOpenColor: colors.lightPinkMessageBg,
    },
    ddxtheme: {
      cardStyle: { backgroundColor: `${colors.ddxAccent}14` },
      iconOpenColor: colors.ddxAccentBright,
    },
    medilearntheme: {
      cardStyle: { backgroundColor: `${colors.medilearnAccent}14` },
      iconOpenColor: colors.ddxPageBg,
    },
  }

  const faqBorderGradientMap: Record<SectionTheme, string> = {
    default:         'linear-gradient(70deg, rgb(114 97 209 / 25%), rgb(30 29 36 / 30%), rgb(114 97 209 / 25%))',
    greentheme:      'linear-gradient(70deg, rgb(34 197 94 / 32%), rgb(26 32 28 / 30%), rgb(34 197 94 / 32%))',
    rabbitxtheme:    'linear-gradient(70deg, rgb(239 68 68 / 16%), rgb(37 21 21 / 31%), rgb(239 68 68 / 16%))',
    arbventuretheme: 'linear-gradient(70deg, #5f993736, #5f993736, #5f993736)',
    lightpinktheme:  'linear-gradient(130deg, rgba(232,180,240,0.55), rgba(232,180,240,0.55) rgba(232,180,240,0.55) )',
    ddxtheme: 'linear-gradient(130deg, rgba(155, 63, 212, 0.356), rgba(43,9,70,0.35), rgba(94,15,155,0.45))',
    medilearntheme: 'linear-gradient(130deg, rgba(155, 176, 105, 0.877), rgba(155, 176, 105, 0.877), rgba(155, 176, 105, 0.877)',
  }

  const faqCardBgMap: Record<SectionTheme, string> = {
    default:         '#0F0A26',
    greentheme:      '#0a1b10',
    rabbitxtheme:    '#130d2bdf',
    arbventuretheme: '#0e1a08',
    lightpinktheme:  'rgba(231,164,233,0.2)',
    ddxtheme: 'rgba(92, 27, 146, 0.932)',
    medilearntheme: 'rgba(156, 175, 115, 0.726)',
  }

  const selectedTheme = themeStyles[theme] ?? themeStyles.default
  const iconColor = isOpen ? selectedTheme.iconOpenColor : '#FFFFFF'

  return (
    <div
      className="p-[1px] rounded-xl"
      style={{ background: faqBorderGradientMap[theme] }}
    >
      <div
        className="rounded-xl py-3 px-4 md:py-4 lg:py-4 xl:py-5 2xl:py-6 md:px-6 transition-all duration-300 overflow-hidden"
        style={{
          ...selectedTheme.cardStyle,
          backgroundColor: faqCardBgMap[theme],
        }}
      >
        {/* QUESTION — full-width tap target */}
        <button
          type="button"
          onClick={onToggle}
          className="w-full flex items-center justify-between gap-3 text-left bg-transparent border-none outline-none cursor-pointer"
        >
          <span className="text-white font-gilroy-medium text-start text-sm md:text-base 2xl:text-xl">
            {question}
          </span>

          <span
            className="flex-shrink-0 h-4 w-4 min-w-4"
            style={{
              WebkitMaskImage: `url(${faqarrow})`,
              maskImage: `url(${faqarrow})`,
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskSize: 'contain',
              maskSize: 'contain',
              backgroundColor: iconColor,
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.5s ease, background-color 0.3s ease',
            }}
          />
        </button>

        {/* ANSWER (animated via max-height) */}
        <div
          className="overflow-hidden"
          style={{
            maxHeight: isOpen ? '500px' : '0px',
            opacity: isOpen ? 1 : 0,
            transition: 'max-height 0.5s ease-in-out, opacity 0.4s ease-in-out',
          }}
        >
          <p className="mt-2 font-gilroy-regular text-start text-white/65 text-sm md:text-sm 2xl:text-base">
            {answer}
          </p>
        </div>
      </div>
    </div>
  )
}

export default QuestionItem